/**
 * Pure H OS · Cost Centers · Management view
 * -----------------------------------------------------------------
 * Iteration 130 · Owner-approved scope:
 *   • View · Edit name · Edit description · Edit active · Edit parent (safe)
 *   • View linked Product / Formula / Raw Material records
 *   • Codes are LOCKED (Numbering service)
 *   • Product cost centers cannot be manually created or deleted
 *   • Deactivate instead of delete · no delete when linked
 *
 * No new engines. No Inventory. No Finance. No Reporting.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, SectionHeader } from "../components/lib";
import { Drawer, Field, Toggle } from "../components/lib.overlay";

type Level = "company" | "product_type" | "category" | "product";

type CC = {
  id: string;
  ventureId: string;
  code: string;
  level: Level;
  name: string;
  description?: string | null;
  parentId?: string | null;
  tag?: string | null;
  productMasterId?: string | null;
  active?: boolean;
};

type Tree = {
  company: CC[];
  category: CC[];
  productType: CC[];
  product: CC[];
};

type LinkedResponse = {
  counts: { products: number; formulas: number; rawMaterials: number; total: number };
  products:     Array<{ id: string; productCode: string; productNumber?: string; nameEn?: string; status?: string }>;
  formulas:     Array<{ id: string; code: string; docNumber?: string; version?: number; status?: string }>;
  rawMaterials: Array<{ id: string; code: string; docNumber?: string; name?: string; status?: string }>;
};

const API = (import.meta as unknown as { env?: { VITE_BACKEND_URL?: string } }).env?.VITE_BACKEND_URL
  || process.env.REACT_APP_BACKEND_URL
  || "";

const LEVEL_LABELS: Record<Level, string> = {
  company: "Company",
  product_type: "Product Type",
  category: "Category",
  product: "Product",
};

export default function CostCenters() {
  const venture = "orvan";
  const [tree, setTree] = useState<Tree | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<boolean>(false);

  // Edit drawer state
  const [editing, setEditing] = useState<CC | null>(null);
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [active, setActive] = useState<boolean>(true);
  const [parentId, setParentId] = useState<string>("");
  const [linked, setLinked] = useState<LinkedResponse | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fetchTree = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(
        `${API}/api/pureh/cost-centers/ventures/${venture}/cost-centers/tree`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      setTree(await r.json());
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void fetchTree(); }, [fetchTree]);

  const allRows = useMemo(() => {
    if (!tree) return [] as CC[];
    return [...tree.company, ...tree.productType, ...tree.category, ...tree.product];
  }, [tree]);

  const byId = useMemo(() => {
    const m = new Map<string, CC>();
    for (const r of allRows) m.set(r.id, r);
    return m;
  }, [allRows]);

  // Children of a given node id
  const childrenOf = useCallback(
    (id: string | null) => allRows.filter((r) => (r.parentId || null) === id),
    [allRows],
  );

  // Valid parent options for the row being edited (per hierarchy invariant).
  const validParents = useMemo(() => {
    if (!editing || !tree) return [] as CC[];
    if (editing.level === "company") return [];
    const expected: Record<Exclude<Level, "company">, Level> = {
      category:     "company",
      product_type: "company",
      product:      "product_type",
    };
    const parentLevel = expected[editing.level as Exclude<Level, "company">];
    if (parentLevel === "company") return tree.company;
    if (parentLevel === "product_type") return tree.productType;
    return [];
  }, [editing, tree]);

  // Flatten the tree in JS so JSX stays non-recursive (avoids
  // emergentbase visual-edits Babel plugin call-stack overflow).
  type FlatNode = { node: CC; depth: number; hasKids: boolean; orphan: boolean };
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const flat = useMemo<FlatNode[]>(() => {
    if (!tree) return [];
    const out: FlatNode[] = [];
    const walk = (n: CC, depth: number, orphan = false) => {
      const kids = childrenOf(n.id);
      out.push({ node: n, depth, hasKids: kids.length > 0, orphan });
      if (collapsed.has(n.id)) return;
      for (const k of kids) walk(k, depth + 1);
    };
    for (const c of tree.company) walk(c, 0);
    // Orphans (level != company but parentId=null) — safety net.
    for (const r of childrenOf(null)) {
      if (r.level !== "company") walk(r, 0, true);
    }
    return out;
  }, [tree, childrenOf, collapsed]);

  const toggleNode = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openEdit = useCallback(async (cc: CC) => {
    setEditing(cc);
    setName(cc.name || "");
    setDescription(cc.description || "");
    setActive(cc.active !== false);
    setParentId(cc.parentId || "");
    setLinked(null);
    setSaveError(null);
    try {
      const r = await fetch(
        `${API}/api/pureh/cost-centers/ventures/${venture}/cost-centers/${cc.id}/linked`,
        { credentials: "include" },
      );
      if (r.ok) setLinked(await r.json());
    } catch { /* linked panel is best-effort */ }
  }, []);

  const closeEdit = () => {
    setEditing(null);
    setSaveError(null);
  };

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    setSaveError(null);
    try {
      const body: Record<string, unknown> = {};
      if (name.trim() !== (editing.name || "")) body.name = name.trim();
      if ((description || "") !== (editing.description || "")) {
        body.description = description;
      }
      if (active !== (editing.active !== false)) body.active = active;
      if ((parentId || "") !== (editing.parentId || "")) {
        body.parentId = parentId || null;
      }
      if (Object.keys(body).length === 0) {
        closeEdit();
        return;
      }
      const r = await fetch(
        `${API}/api/pureh/cost-centers/ventures/${venture}/cost-centers/${editing.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(body),
        },
      );
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `${r.status} ${r.statusText}`);
      }
      await fetchTree();
      closeEdit();
    } catch (e) {
      setSaveError(String((e as Error).message || e));
    } finally {
      setBusy(false);
    }
  };

  const isReadOnlyName = editing?.level === "product";  // display auto-syncs from PM
  const isReadOnlyParent = editing?.level === "company"; // company has no parent

  return (
    <div className="space-y-6" data-testid="cost-centers-page">
      <Card testId="cc-header">
        <SectionHeader
          eyebrow="Foundation · Cost Center Hierarchy"
          title="Cost Centers"
        />
        <div className="text-xs mt-1" style={{ color: "var(--ph-muted)" }}>
          Company → Product Type → Category → Product ·
          venture <span className="font-medium">{venture.toUpperCase()}</span> ·
          codes locked by the central Numbering service.
        </div>
        {error && (
          <div className="text-xs mt-2" style={{ color: "var(--ph-danger, #b8523a)" }} data-testid="cc-error">
            {error}
          </div>
        )}
      </Card>

      {/* Hierarchical tree · Company → Product Type → Category → Product */}
      <Card padded={false} testId="cc-tree">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader
            eyebrow="Hierarchy"
            title={loading ? "Loading…" : `${allRows.length} cost center${allRows.length === 1 ? "" : "s"}`}
          />
        </div>
        <div className="p-2" data-testid="cc-tree-body">
          {flat.map(({ node, depth, hasKids, orphan }) => {
            const isCollapsed = collapsed.has(node.id);
            const activeColor = node.active === false ? "var(--ph-muted)" : "var(--ph-text)";
            return (
              <div
                key={node.id}
                data-testid={`cc-node-${node.code}`}
                className="px-3 py-2 flex items-center gap-3 border-b hover:bg-black/[0.02]"
                style={{
                  borderColor: "var(--ph-border-subtle)",
                  paddingLeft: `${12 + depth * 20}px`,
                  color: activeColor,
                }}
              >
                {hasKids ? (
                  <button
                    type="button"
                    onClick={() => toggleNode(node.id)}
                    className="text-xs w-4 h-4 flex items-center justify-center rounded"
                    style={{ color: "var(--ph-muted)" }}
                    data-testid={`cc-toggle-${node.code}`}
                  >
                    {isCollapsed ? "▸" : "▾"}
                  </button>
                ) : (
                  <span className="w-4" />
                )}
                <span
                  className="uppercase tracking-[0.14em] text-[10px] shrink-0 font-mono"
                  style={{ color: "var(--ph-muted)", width: 200 }}
                >
                  {node.code}
                </span>
                <span
                  className="text-[10px] uppercase tracking-[0.14em] shrink-0"
                  style={{ color: "var(--ph-muted)", width: 100 }}
                >
                  {LEVEL_LABELS[node.level] || node.level}
                </span>
                <span className="text-sm flex-1 min-w-0 truncate">
                  {node.name}
                  {node.active === false && (
                    <span className="ml-2 text-[10px] uppercase" style={{ color: "var(--ph-muted)" }}>
                      inactive
                    </span>
                  )}
                  {orphan && (
                    <span className="ml-2 text-[10px] uppercase" style={{ color: "var(--ph-danger, #b8523a)" }}>
                      orphan
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => openEdit(node)}
                  className="text-xs px-2 py-1 rounded"
                  style={{ border: "1px solid var(--ph-border-subtle)", color: "var(--ph-text)" }}
                  data-testid={`cc-edit-${node.code}`}
                >
                  Edit
                </button>
              </div>
            );
          })}
          {flat.length === 0 && !loading && (
            <div className="p-6 text-center text-xs" style={{ color: "var(--ph-muted)" }}>
              No cost centers found for this venture.
            </div>
          )}
        </div>
      </Card>

      {/* Edit drawer */}
      <Drawer
        open={!!editing}
        onClose={closeEdit}
        title={editing ? `Edit · ${editing.code}` : "Edit"}
        testId="cc-edit-drawer"
      >
        {editing && (
          <div className="space-y-1" data-testid="cc-edit-body">
            {/* Meta strip — code, level, tag, PM link — all read-only. */}
            <div className="rounded-md p-3 mb-3" style={{ background: "var(--ph-surface-subtle, #f6f2ea)" }}>
              <div className="flex justify-between text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-muted)" }}>
                <span>Code (locked)</span><span>Level</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="font-mono" data-testid="cc-edit-code">{editing.code}</span>
                <span>{LEVEL_LABELS[editing.level] || editing.level}</span>
              </div>
              {editing.tag && (
                <div className="text-xs mt-2" style={{ color: "var(--ph-muted)" }}>
                  Tag: <span className="font-mono">{editing.tag}</span>
                </div>
              )}
              {editing.productMasterId && (
                <div className="text-xs mt-1" style={{ color: "var(--ph-muted)" }}>
                  Linked product master: <span className="font-mono" data-testid="cc-edit-pmid">{editing.productMasterId}</span>
                </div>
              )}
            </div>

            <Field label="Display name" hint={isReadOnlyName ? "Auto-synced from Product Master · not editable here." : "Rename freely (e.g. Perfume → Fine Fragrance · Enfant → Kids)."}>
              <input
                type="text"
                className="w-full px-3 py-2 rounded border text-sm"
                style={{ borderColor: "var(--ph-border-subtle)" }}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isReadOnlyName || busy}
                data-testid="cc-edit-name"
              />
            </Field>

            <Field label="Description" hint="Optional internal note about what this cost center covers.">
              <textarea
                className="w-full px-3 py-2 rounded border text-sm"
                style={{ borderColor: "var(--ph-border-subtle)", minHeight: 64 }}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={busy}
                data-testid="cc-edit-description"
              />
            </Field>

            <Field label="Active" hint="Deactivate to hide from future dropdowns without deleting.">
              <Toggle
                checked={active}
                onChange={setActive}
                testId="cc-edit-active"
              />
            </Field>

            {!isReadOnlyParent && (
              <Field label="Parent" hint="Change parent only within the same level matrix. System validates before saving.">
                <select
                  className="w-full px-3 py-2 rounded border text-sm bg-white"
                  style={{ borderColor: "var(--ph-border-subtle)" }}
                  value={parentId}
                  onChange={(e) => setParentId(e.target.value)}
                  disabled={busy}
                  data-testid="cc-edit-parent"
                >
                  {validParents.map((p) => (
                    <option key={p.id} value={p.id}>{p.code} · {p.name}</option>
                  ))}
                </select>
              </Field>
            )}

            {/* Linked records viewer */}
            {linked && (
              <div className="mt-4" data-testid="cc-edit-linked">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-muted)" }}>
                  Linked records ({linked.counts.total})
                </div>
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <LinkedBox label="Products"      count={linked.counts.products} />
                  <LinkedBox label="Formulas"      count={linked.counts.formulas} />
                  <LinkedBox label="Raw materials" count={linked.counts.rawMaterials} />
                </div>
                {linked.products.length > 0 && (
                  <div className="mt-3" data-testid="cc-linked-products">
                    <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-muted)" }}>Products</div>
                    {linked.products.slice(0, 8).map((p) => (
                      <div key={p.id} className="text-xs py-0.5">
                        <span className="font-mono">{p.productNumber || p.productCode}</span> · {p.nameEn || p.productCode}
                        {p.status && p.status !== "active" && (
                          <span className="ml-2 text-[10px]" style={{ color: "var(--ph-muted)" }}>{p.status}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {linked.formulas.length > 0 && (
                  <div className="mt-2" data-testid="cc-linked-formulas">
                    <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-muted)" }}>Formulas</div>
                    {linked.formulas.slice(0, 8).map((f) => (
                      <div key={f.id} className="text-xs py-0.5">
                        <span className="font-mono">{f.docNumber || f.code}</span> · v{f.version} · {f.status}
                      </div>
                    ))}
                  </div>
                )}
                {linked.rawMaterials.length > 0 && (
                  <div className="mt-2" data-testid="cc-linked-rawmaterials">
                    <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-muted)" }}>Raw materials</div>
                    {linked.rawMaterials.slice(0, 8).map((rm) => (
                      <div key={rm.id} className="text-xs py-0.5">
                        <span className="font-mono">{rm.docNumber || rm.code}</span> · {rm.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {saveError && (
              <div className="text-xs mt-3 p-2 rounded"
                style={{ background: "var(--ph-danger-soft, #fbe9e2)", color: "var(--ph-danger, #b8523a)" }}
                data-testid="cc-save-error"
              >
                {saveError}
              </div>
            )}

            <div className="flex gap-2 mt-6">
              <button
                type="button"
                onClick={save}
                disabled={busy}
                className="px-4 py-2 rounded text-sm font-medium"
                style={{ background: "var(--ph-accent, #25230F)", color: "white", opacity: busy ? 0.5 : 1 }}
                data-testid="cc-save-btn"
              >
                {busy ? "Saving…" : "Save changes"}
              </button>
              <button
                type="button"
                onClick={closeEdit}
                disabled={busy}
                className="px-4 py-2 rounded text-sm"
                style={{ border: "1px solid var(--ph-border-subtle)" }}
                data-testid="cc-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

/* ─── Sub-components ───────────────────────────────────────── */

function LinkedBox({ label, count }: { label: string; count: number }) {
  return (
    <div
      className="p-2 rounded"
      style={{ background: "var(--ph-surface-subtle, #f6f2ea)" }}
    >
      <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-muted)" }}>{label}</div>
      <div className="text-lg font-mono mt-1" style={{ color: "var(--ph-text)" }}>{count}</div>
    </div>
  );
}
